/*------------------------------------------------------------------------------
   About      : UI state for the NBI Insights screen (epic DA-697, II-FE-1)

                Holds the merged row set, the local filter/sort/page state and
                the selection model. The catalog is NOT here — it lives in
                `useInsightCatalog`'s module cache, because it is global and
                read-only and does not belong in per-pilot UI state.

                ⚠️ Paging is client-side. `getInsights` has no page/pageSize/
                sort/search params and returns the whole pilot partitioned three
                ways, so `page` here indexes into the locally filtered rows —
                it is never sent to the server.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { createSlice, PayloadAction }  from '@reduxjs/toolkit'
import { RootState }                   from 'store/Store'
import { CONTENT_EDIT_STATUS }         from 'types/cms/frontendTypes'
import { GetInsightsResponse,
         InsightRecord,
         InsightReviewWorkflow }       from '../types/insightTypes'

export interface InsightFilters {
  /** `editStatus` — Draft / Modified / Published. '' = all. */
  editStatus   : string
  type         : string
  applianceId  : string
  category     : string
  validation   : string
  search       : string
}

export interface InsightUIState {
  insights        : InsightRecord[]
  /**
   * The `published` partition, kept keyed by documentId ALONGSIDE the merged
   * rows.
   *
   * Needed because the merge collapses a record that appears in both `modified`
   * and `published` down to the modified version, which is right for the table
   * but loses the counterpart the per-field "modified" highlight has to diff
   * against. The reco path does the same thing — `RecoEditFormComponent` looks
   * its published version up out of a separately-held `publishedRecos`.
   */
  publishedById   : Record<string, InsightRecord>
  reviewWorkflow  : InsightReviewWorkflow | null
  filters         : InsightFilters
  page            : number
  pageSize        : number
  selectedIds     : string[]
  isLoading       : boolean
  error          ?: string
}

const defaultFilters : InsightFilters = {
  editStatus  : '',
  type        : '',
  applianceId : '',
  category    : '',
  validation  : '',
  search      : '',
}

const initialState : InsightUIState = {
  insights       : [],
  publishedById  : {},
  reviewWorkflow : null,
  filters        : defaultFilters,
  page           : 1,
  pageSize       : 25,
  selectedIds    : [],
  isLoading      : false,
  error          : undefined,
}

/**
 * Flattens the three partitions into one row set, stamping `editStatus` from
 * whichever partition a record arrived in.
 *
 * Order matters. A record that is both published and has an unpublished draft
 * appears in `modified` AND `published`; the modified view is the one the author
 * is working on, so it wins. Keyed on `documentId`, never on `code` — `code` is
 * unique per pilot, not globally, and a cross-pilot merge on it would collapse
 * legitimately distinct records.
 */
export function mergeInsightPartitions(payload: GetInsightsResponse): InsightRecord[] {
  const byDocumentId = new Map<string, InsightRecord>()

  const absorb = (rows: InsightRecord[] | undefined, editStatus: CONTENT_EDIT_STATUS) => {
    for (const row of rows ?? []) {
      if (!row?.documentId || byDocumentId.has(row.documentId)) continue
      byDocumentId.set(row.documentId, { ...row, editStatus })
    }
  }

  absorb(payload.modified,  CONTENT_EDIT_STATUS.MODIFIED)
  absorb(payload.draft,     CONTENT_EDIT_STATUS.DRAFT)
  absorb(payload.published, CONTENT_EDIT_STATUS.PUBLISHED)

  return [...byDocumentId.values()]
}

export const insightUISlice = createSlice({
  name : 'insightUI',
  initialState,
  reducers : {
    setInsights : (state, action: PayloadAction<InsightRecord[]>) => {
      state.insights = action.payload
      // Drop selections for rows that no longer exist, or a bulk action fires
      // against a documentId the server has since deleted.
      const present = new Set(action.payload.map((i) => i.documentId))
      state.selectedIds = state.selectedIds.filter((id) => present.has(id))
    },

    setInsightsFromPartitions : (state, action: PayloadAction<GetInsightsResponse>) => {
      const merged = mergeInsightPartitions(action.payload)
      state.insights = merged

      // Retained un-merged so the edit screen can diff a modified record
      // against what is actually live. Stamped PUBLISHED because these rows come
      // from that partition by definition.
      state.publishedById = {}
      for (const row of action.payload.published ?? []) {
        if (row?.documentId) {
          state.publishedById[row.documentId] = { ...row, editStatus : CONTENT_EDIT_STATUS.PUBLISHED }
        }
      }

      const present = new Set(merged.map((i) => i.documentId))
      state.selectedIds = state.selectedIds.filter((id) => present.has(id))
    },

    /** Replaces one row in place — used after a save, publish or tag response. */
    /**
     * Replaces one row from a server response — **merging, not overwriting**.
     *
     * ⚠️ `allowedTransitions` is computed by only two endpoints: `getInsights`
     * and `tagRecommendations`. `updateAll`, `publishAll`, `unpublishAll` and
     * `discardAllDrafts` all return the record WITHOUT it.
     *
     * So a straight assignment silently destroyed it: saving an edit, toggling
     * Active or changing QA status replaced a record that had transitions with
     * one that had none, and the QA dropdown collapsed to just the current
     * status until the page was reloaded.
     *
     * Fields absent from the incoming payload are therefore kept. A caller that
     * needs genuinely fresh transitions — a QA status change, where they change
     * by definition — must refetch; see `useInsightWorkflowActions`.
     */
    upsertInsight : (state, action: PayloadAction<InsightRecord>) => {
      const incoming = action.payload
      const index    = state.insights.findIndex((i) => i.documentId === incoming.documentId)

      if (index === -1) {
        state.insights.push(incoming)
        return
      }

      const existing = state.insights[index]
      state.insights[index] = {
        ...existing,
        ...incoming,
        // `in` rather than a truthiness check: an empty array is a meaningful
        // answer ("no transitions available"), and must not be mistaken for
        // the field being absent.
        allowedTransitions : 'allowedTransitions' in incoming
          ? incoming.allowedTransitions
          : existing.allowedTransitions,
      }
    },

    removeInsight : (state, action: PayloadAction<string>) => {
      state.insights   = state.insights.filter((i) => i.documentId !== action.payload)
      state.selectedIds = state.selectedIds.filter((id) => id !== action.payload)
    },

    setReviewWorkflow : (state, action: PayloadAction<InsightReviewWorkflow | null>) => {
      state.reviewWorkflow = action.payload
    },

    setFilters : (state, action: PayloadAction<Partial<InsightFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.page = 1 // a narrowed result set makes the old page index meaningless
    },

    resetFilters : (state) => {
      state.filters = defaultFilters
      state.page = 1
    },

    setPage : (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },

    setPageSize : (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
      state.page = 1
    },

    setSelectedIds : (state, action: PayloadAction<string[]>) => {
      state.selectedIds = action.payload
    },

    clearSelection : (state) => {
      state.selectedIds = []
    },

    setIsLoading : (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    setError : (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload
    },

    resetInsightUI : () => initialState,
  },
})

export const {
  setInsights,
  setInsightsFromPartitions,
  upsertInsight,
  removeInsight,
  setReviewWorkflow,
  setFilters,
  resetFilters,
  setPage,
  setPageSize,
  setSelectedIds,
  clearSelection,
  setIsLoading,
  setError,
  resetInsightUI,
} = insightUISlice.actions

/*------------------------------------------------------------------------------
   Selectors
------------------------------------------------------------------------------*/

const selectSlice = (state: RootState) =>
  (state as any).insightUI as InsightUIState | undefined

export const selectInsights       = (state: RootState) => selectSlice(state)?.insights ?? []
export const selectPublishedById  = (state: RootState) => selectSlice(state)?.publishedById ?? {}

/** One insight from the merged row set, or undefined while the list loads. */
export const selectInsightById = (documentId: string | undefined) => (state: RootState) =>
  documentId ? selectSlice(state)?.insights.find((i) => i.documentId === documentId) : undefined

/** The live version of a record, for the per-field modified highlight. */
export const selectPublishedVersion = (documentId: string | undefined) => (state: RootState) =>
  documentId ? selectSlice(state)?.publishedById[documentId] : undefined
export const selectReviewWorkflow = (state: RootState) => selectSlice(state)?.reviewWorkflow ?? null
export const selectInsightFilters = (state: RootState) => selectSlice(state)?.filters ?? defaultFilters
export const selectInsightPage    = (state: RootState) => selectSlice(state)?.page ?? 1
export const selectInsightPageSize= (state: RootState) => selectSlice(state)?.pageSize ?? 25
export const selectSelectedIds    = (state: RootState) => selectSlice(state)?.selectedIds ?? []
export const selectInsightsLoading= (state: RootState) => selectSlice(state)?.isLoading ?? false
export const selectInsightsError  = (state: RootState) => selectSlice(state)?.error

export const selectHasActiveInsightFilters = (state: RootState) => {
  const f = selectSlice(state)?.filters
  if (!f) return false
  return !!(f.editStatus || f.type || f.applianceId || f.category || f.validation || f.search)
}

/**
 * Status labels come from the workflow table, not a hardcoded map:
 * `READY_FOR_QA` renders as "Ready for review" while the stored value is
 * unchanged, because CONTENT_VERIFICATION_STATUS is shared with recommendations
 * and surveys and cannot be renamed for insights alone.
 */
export const selectStatusLabels = (state: RootState) =>
  selectSlice(state)?.reviewWorkflow?.[0]?.statusLabels ?? {}
